/**
 * Route → required-permission map for defense-in-depth UI gating (Task 9).
 *
 * Permission strings are copied VERBATIM from pam_permissions.go's Permission
 * constants (the wire values, e.g. `PermAuditQuery = "audit:query"`) —
 * cross-checked against docs/admin-rbac-permission-matrix.md. Never invent a
 * string here; if a route's dominant handler has no corresponding Permission
 * constant, the route is deliberately left OUT of this map (see below).
 *
 * This is DEFENSE IN DEPTH ONLY. The actual enforcement point is the server
 * (enforcePermission / resolvePermissionSet in admin_authz_helpers.go) — a
 * UI check here can only hide/deny cosmetically; it must never be treated as
 * a substitute for the server 403. `ui/e2e/rbac-harness.spec.ts` proves the
 * server enforces these gates independently of anything in this file.
 *
 * ## Why not every /admin/* route is listed
 *
 * Not every admin page maps 1:1 onto the fine-grained Permission catalog:
 * some surfaces (Identity: subjects/resources/apps/entitlements; core
 * multi-tenant admin: tenants/users/connectors/clients/mtls-cas/api-docs;
 * PAM overview/security-keys; CAE/resilience dashboards; and the Governance
 * discovery/campaigns/roles/dual-auth pages, which are intentionally visible
 * to every admin role) either have no dedicated Permission constant in
 * pam_permissions.go yet, or are/should-remain visible to any resolved admin
 * role. Those routes fall back to AdminLayout's NAV_ROLE_FILTER (role-kind
 * based) or are gated only by the base admin session (ProtectedRoute's
 * `requireAdmin`) — see AdminLayout.tsx's `isNavItemVisible` for the
 * combined decision.
 *
 * Consumed by:
 *  - src/App.tsx           — ProtectedRoute's `requiredPermission` prop per route.
 *  - src/layout/AdminLayout.tsx — `isNavItemVisible`'s nav-hiding decision.
 * Sharing one map keeps "can reach the route" and "can see the nav link" in
 * sync so a permission change can never desync hidden-vs-reachable.
 */
export const ROUTE_PERMISSIONS: Record<string, string> = {
  // Tenant infra credential-backend configuration — security-admin only.
  '/admin/secrets-provider': 'tenant:secrets.manage',
  '/admin/mail-provider': 'tenant:mail.manage',

  // Audit — audit-admin only.
  '/admin/audit': 'audit:query',
  // Chain-integrity diagnosis (AdminGetAuditChainIntegrity,
  // admin_audit_integrity_server.go) is gated server-side by the SAME
  // PermAuditQuery as AdminQueryAudit — verified at the handler's
  // enforceAuditPermission(r, req.TenantID, PermAuditQuery) call.
  '/admin/audit-chain-integrity': 'audit:query',
  '/admin/audit-evidence': 'accessreq:evidence_package.get',
  '/admin/audit-evidence/:requestId': 'accessreq:evidence_package.get',

  // Policies / step-up (MFA) policy — policy-admin only.
  '/admin/policies': 'policy:list',
  '/admin/stepup-policy': 'mfa_policy:get',

  // Access requests — security-admin only.
  '/admin/access-requests': 'accessreq:list',
  '/admin/access-requests/:requestId': 'accessreq:get',
  '/admin/access-requests/:requestId/evidence': 'accessreq:evidence.get',

  // Break-glass / emergency roles — security-admin only.
  '/admin/break-glass': 'breakglass:event.list_unreviewed',
  '/admin/emergency-roles': 'breakglass:role.list',

  // Brokered sessions / session control policy — security-admin only.
  '/admin/brokered-sessions': 'session:list',
  '/admin/brokered-sessions/:sessionId': 'session:get',
  '/admin/session-control-policy': 'session:control_policy.get',

  // Governance role registry — audit-admin only (gov:roledef.manage).
  '/admin/governance/role-registry': 'gov:roledef.manage',

  // Per-user factor management (AdminListUserFactors / AdminRevokeUserFactor,
  // admin_user_factors.go) — gated server-side by PermTenantUsersFactorsManage
  // (pam_permissions.go). Previously left ungated here/in App.tsx beyond the
  // outer requireAdmin because no dedicated Permission constant existed yet;
  // PermTenantUsersFactorsManage was added for exactly this handler, so this
  // route can now close the "nav-hidden but not route-gated, direct URL
  // renders it" gap (fix-wave 2026-07-24 CRITICAL-1 ledger item).
  '/admin/users/:userId/factors': 'tenant:users.factors_manage',
}
